import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Copy, Gift, Users, Clock, CheckCircle, Share2 } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

interface ReferralCode {
  code: string
  is_active: boolean
  expires_at: string | null
  share_url: string
}

interface ReferralStats {
  total_referrals: number
  total_rewards_earned: number
  rewards_available: number
  rewards_used: number
  rewards_expired: number
  total_discount_given: number
}

interface ReferralHistory {
  id: string
  referred_user: {
    id: string
    name: string
    email: string
  } | null
  status: string
  created_at: string
}

interface Reward {
  id: string
  type: string
  type_label: string
  reward_value: number
  status: string
  earned_at: string
  expires_at: string
  used_at: string | null
}

interface RewardsData {
  rewards: Reward[]
  available_rewards: Reward[]
  has_signup_bonus: boolean
}

async function fetchReferralCode(): Promise<ReferralCode> {
  const response = await fetch('/api/v1/user/referrals/code', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch referral code')
  const data = await response.json()
  return data.data
}

async function fetchReferralStats(): Promise<ReferralStats> {
  const response = await fetch('/api/v1/user/referrals/stats', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch referral stats')
  const data = await response.json()
  return data.data
}

async function fetchReferralHistory(): Promise<ReferralHistory[]> {
  const response = await fetch('/api/v1/user/referrals/history', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch referral history')
  const data = await response.json()
  return data.data
}

async function fetchRewards(): Promise<RewardsData> {
  const response = await fetch('/api/v1/user/referrals/rewards', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch rewards')
  const data = await response.json()
  return data.data
}

export default function ReferralPage() {
  const embedded = useDashboardEmbedded()
  const [copied, setCopied] = useState(false)

  const { data: referralCode, isLoading: codeLoading } = useQuery({
    queryKey: ['referral-code'],
    queryFn: fetchReferralCode,
  })

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['referral-stats'],
    queryFn: fetchReferralStats,
  })

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['referral-history'],
    queryFn: fetchReferralHistory,
  })

  const { data: rewardsData } = useQuery({
    queryKey: ['referral-rewards'],
    queryFn: fetchRewards,
  })

  const handleCopyCode = async () => {
    if (referralCode?.code) {
      await navigator.clipboard.writeText(referralCode.code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleShare = async () => {
    if (referralCode?.share_url) {
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join me on PortfolioLeaf!',
            text: 'Use my referral code to get 1 month free!',
            url: referralCode.share_url,
          })
        } catch (err) {
          // User cancelled or share failed
        }
      } else {
        await navigator.clipboard.writeText(referralCode.share_url)
        alert('Referral link copied to clipboard!')
      }
    }
  }

  if (codeLoading || statsLoading) {
    return <div className="p-6">Loading...</div>
  }

  const availableRewards = rewardsData?.available_rewards || []

  return (
    <div className="p-6 space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold mb-2">Referral Program</h1>
          <p className="text-muted-foreground">
            Invite friends and both of you get 1 month free!
          </p>
        </div>
      )}

      {/* Your Referral Code */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Your Referral Code
          </CardTitle>
          <CardDescription>
            Share this code with friends - you both get 1 month free!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 p-4 bg-muted rounded-lg">
              <p className="text-2xl font-mono font-bold tracking-wider">
                {referralCode?.code || 'XXXXXX'}
              </p>
            </div>
            <Button variant="outline" onClick={handleCopyCode}>
              <Copy className="w-4 h-4 mr-2" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button onClick={handleShare}>
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Share link: <span className="text-primary">{referralCode?.share_url}</span>
          </p>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Referrals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              <span className="text-2xl font-bold">{stats?.total_referrals || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Rewards Earned
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-500" />
              <span className="text-2xl font-bold">{stats?.rewards_available || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Rewards Used
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-500" />
              <span className="text-2xl font-bold">{stats?.rewards_used || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Discount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">
                {(stats?.total_discount_given || 0) / 100}%
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="font-bold">1</span>
              </div>
              <div>
                <h4 className="font-medium">Share Your Code</h4>
                <p className="text-sm text-muted-foreground">
                  Share your unique referral code with friends
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="font-bold">2</span>
              </div>
              <div>
                <h4 className="font-medium">Friend Signs Up</h4>
                <p className="text-sm text-muted-foreground">
                  They use your code and get 1 month free
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="font-bold">3</span>
              </div>
              <div>
                <h4 className="font-medium">You Get Rewarded</h4>
                <p className="text-sm text-muted-foreground">
                  You get 1 month free on your next billing!
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Rewards */}
      {availableRewards.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-500" />
              Available Rewards
            </CardTitle>
            <CardDescription>
              These rewards will be automatically applied to your next subscription
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {availableRewards.map((reward) => (
                <div
                  key={reward.id}
                  className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium">{reward.type_label}</p>
                      <p className="text-sm text-green-600">
                        {reward.reward_value}% discount for 1 month
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Expires {new Date(reward.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referral History */}
      <Card>
        <CardHeader>
          <CardTitle>Referral History</CardTitle>
          <CardDescription>People you've referred</CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : history && history.length > 0 ? (
            <div className="space-y-4">
              {history.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">
                        {referral.referred_user?.name || 'User'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {referral.referred_user?.email}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        referral.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {referral.status
                        ? referral.status.charAt(0).toUpperCase() + referral.status.slice(1)
                        : 'Pending'}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(referral.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No referrals yet. Share your code to start earning rewards!
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
